import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { calculateCompliance } from '@/lib/analytics-calculations';

interface CompliancePoint {
  week_start: string; planned_count: number; completed_count: number;
  planned_minutes: number; completed_minutes: number; planned_load: number; completed_load: number;
}
interface WorkoutComplianceChartProps { data: CompliancePoint[]; }

export function WorkoutComplianceChart({ data }: WorkoutComplianceChartProps) {
  const enriched = useMemo(() => data.map((w) => {
    const score = calculateCompliance(
      { count: w.planned_count, load: w.planned_load, minutes: w.planned_minutes },
      { count: w.completed_count, load: w.completed_load, minutes: w.completed_minutes },
    );
    return { weekLabel: w.week_start.slice(5), planned: w.planned_load, completed: w.completed_load, compliancePct: score.overallPct };
  }), [data]);

  const totals = useMemo(() => {
    const t = data.reduce((acc, w) => ({
      planned: acc.planned + w.planned_count, completed: acc.completed + w.completed_count,
      plannedLoad: acc.plannedLoad + w.planned_load, completedLoad: acc.completedLoad + w.completed_load,
      plannedMins: acc.plannedMins + w.planned_minutes, completedMins: acc.completedMins + w.completed_minutes,
    }), { planned: 0, completed: 0, plannedLoad: 0, completedLoad: 0, plannedMins: 0, completedMins: 0 });
    return calculateCompliance({ count: t.planned, load: t.plannedLoad, minutes: t.plannedMins }, { count: t.completed, load: t.completedLoad, minutes: t.completedMins });
  }, [data]);

  if (!enriched.length) return null;

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold">Workout Compliance</h3>
        <div className="flex items-center gap-3">
          <Badge label="Sessions" value={totals.sessionsPct} />
          <Badge label="Load" value={totals.loadPct} />
          <Badge label="Overall" value={totals.overallPct} highlight />
        </div>
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={enriched} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="weekLabel" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={35} />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Bar dataKey="planned" fill="hsl(var(--muted-foreground))" opacity={0.3} name="Planned TSS" radius={[3, 3, 0, 0]} />
            <Bar dataKey="completed" name="Completed TSS" radius={[3, 3, 0, 0]}>
              {enriched.map((entry, i) => (
                <Cell key={i} fill={entry.compliancePct >= 90 ? 'hsl(var(--chart-2))' : entry.compliancePct >= 70 ? 'hsl(var(--chart-1))' : 'hsl(var(--chart-4))'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Badge({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  const color = value >= 90 ? 'text-emerald-400' : value >= 70 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="text-center">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`text-sm font-bold tabular-nums ${highlight ? color : 'text-foreground'}`}>{value}%</div>
    </div>
  );
}
